<template>
  <div>
    <!-- Loading -->
    <div v-if="loading" class="text-center p-5">Loading articles...</div>

    <!-- Table -->
    <div v-else>
      <div v-if="articles.length === 0" class="text-center text-muted p-4 border rounded">
        No articles available.
      </div>

      <table v-else class="table table-striped">
        <thead>
          <tr>
            <th>Image</th>
            <th>Title</th>
            <th>Description</th>
            <th>URL</th>
            <th class="text-right">Actions</th>
          </tr>
        </thead>

        <tbody>
          <tr v-for="item in articles" :key="item.id">
            <!-- Image -->
            <td>
              <div v-if="item.urlImage">
                <a :href="item.urlImage" target="_blank" title="View Image">
                  <img
                    :src="item.urlImage"
                    alt="Article Thumbnail"
                    style="width: 40px; height: 40px; object-fit: cover; border-radius: 3px;"
                  />
                </a>
              </div>
              <div v-else class="text-muted small">No image</div>
            </td>

            <!-- Title -->
            <td>{{ item.Title }}</td>

            <!-- Description -->
            <td>{{ item.Description | truncate(80) }}</td>

            <!-- URL -->
            <td>
              <a :href="item.Url" target="_blank">{{ item.Url | truncate(40) }}</a>
            </td>

            <!-- Actions -->
            <td class="text-right">
              <img
                src="../../assets/img/edit-btn.png"
                width="30"
                class="cursor-pointer mr-2"
                @click="openEditModal(item)"
              />
              <img
                src="../../assets/img/delete-btn.png"
                width="30"
                class="cursor-pointer"
                @click="confirmDelete(item.id, item.Title)"
              />
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <!-- Edit Article Popup -->
    <div v-if="openArticle" class="upload-popup-overlay" @click.self="closeUploadPopup">
      <div class="upload-popup-card">
        <button class="close-btn" @click="closeUploadPopup">×</button>
        <h4 class="popup-title text-left">{{ isEdit ? 'Edit Article' : 'Upload Article' }}</h4>

        <!-- Title -->
        <div class="text-left mt-4">
          <label>Title</label>
          <CInput v-model="titleArticle" type="text" placeholder="Enter title..." />
        </div>

        <!-- Description -->
        <div class="text-left">
          <label>Description</label>
          <CTextarea v-model="descriptionArticle" type="text" placeholder="Enter description..." rows="3" />
        </div>

        <!-- Link -->
        <div class="text-left">
          <label>Link</label>
          <CInput v-model="link" type="text" placeholder="Enter link..." />
        </div>

        <!-- Upload Zone -->
        <div class="upload-dropzone" @click="triggerFileInput" @dragover.prevent @drop.prevent="handleDrop">
          <input type="file" ref="fileInput" @change="handleFiles" accept=".png,.jpg,.jpeg" style="display:none" />

          <!-- Preview -->
          <div v-if="previewUrl" class="image-preview">
            <img :src="previewUrl" alt="Preview" class="preview-img" />
          </div>

          <div v-else class="upload-content">
            <img src="../../assets/img/upload.png" alt="Upload Icon" class="upload-icon" />
            <p class="upload-text">Browse File</p>
            <p class="upload-subtitle">Choose a file or drag & drop it here</p>
            <p class="upload-format-text">PNG, JPG, or JPEG formats, up to 10 MB</p>
          </div>
        </div>

        <!-- File Preview -->
        <div v-if="uploadedFile" class="file-preview">
          <span class="file-name">
            <i class="fa fa-paperclip"></i> {{ uploadedFile.name }}
          </span>
          <button class="remove-file" @click="removeFile">×</button>
        </div>

        <!-- Submit -->
        <button
          class="upload-submit-btn"
          :disabled="!titleArticle || !descriptionArticle || !link || isUploading"
          @click="submitFile"
        >
          {{ isUploading ? "Saving..." : "Save" }}
        </button>
      </div>
    </div>
  </div>
</template>
<script>
export default {
  data() {
    return {
      articles: [],
      loading: false,
      openArticle: false,
      selectedArticleId: null,
      titleArticle: "",
      descriptionArticle: "",
      link: "",
      uploadedFile: null,
      previewUrl: null,
      uploadedFileUrl: "",
      uploadedFileId: null,
      isUploading: false,
    };
  },
  methods: {
    async loadArticles() {
      this.loading = true;
      try {
        const res = await this.$API.get(this.$GetListArticles, { withCredentials: true });
        if (res.status === 200 && Array.isArray(res.data)) {
          this.articles = res.data.map((item) => {
            if (item.urlImage && item.urlImage.startsWith("URL: ")) {
              item.urlImage = item.urlImage.substring(5);
            }
            return item;
          });
        }
      } catch (err) {
        console.error(err);
      } finally {
        this.loading = false;
      }
    },

    // Open edit popup and pre-fill fields
    openEditModal(article) {
      this.selectedArticleId = article.id;
      this.titleArticle = article.Title;
      this.descriptionArticle = article.Description;
      this.link = article.Url;
      this.previewUrl = article.urlImage || null;
      this.uploadedFileUrl = article.urlImage || "";
      this.uploadedFileId = article.fileAwsId || null;
      this.openArticle = true;
    },

    // Close popup & reset form
    closeUploadPopup() {
      this.openArticle = false;
      this.selectedArticleId = null;
      this.titleArticle = "";
      this.descriptionArticle = "";
      this.link = "";
      this.uploadedFile = null;
      this.previewUrl = null;
      this.uploadedFileUrl = "";
      this.uploadedFileId = null;
      this.isUploading = false;
    },

    // File input
    triggerFileInput() {
      this.$refs.fileInput.click();
    },
    handleFiles(event) {
      const file = event.target.files[0];
      if (file) {
        this.uploadedFile = file;
        this.previewUrl = URL.createObjectURL(file);
      }
    },
    removeFile() {
      this.uploadedFile = null;
      this.previewUrl = null;
      this.uploadedFileUrl = "";
      this.uploadedFileId = null;
    },

    // Submit file first, then article
    async submitFile() {
      if (!this.uploadedFile) {
        // No new file, just submit article
        return this.submitArticle();
      }

      this.isUploading = true;
      try {
        const formData = new FormData();
        formData.append("files", this.uploadedFile);

        const res = await this.$API.post(this.$UploadArticle, formData, {
          withCredentials: true,
          timeout: 600000,
        });

        const uploaded = res.data[0];

        if (uploaded.success) {
          this.uploadedFileId = uploaded.fileId;

          // Clean URL
          let cleanUrl = uploaded.fileUrl;
          const prefix = "URL: ";
          if (cleanUrl && cleanUrl.startsWith(prefix)) {
            cleanUrl = cleanUrl.substring(prefix.length);
          }
          this.uploadedFileUrl = cleanUrl;

          // ✅ Now submit the article using the uploaded file info
          await this.submitArticle();
          alert("✅ File uploaded and article saved successfully!");
          this.closeUploadPopup();
        } else {
          alert("❌ Upload failed: " + (uploaded.error || "Unknown error"));
        }
      } catch (err) {
        console.error(err);
        alert("⚠️ Error uploading file.");
      } finally {
        this.isUploading = false;
      }
    },

    // Submit article (with uploaded file info)
    async submitArticle() {
      if (!this.titleArticle || !this.descriptionArticle || !this.link) {
        alert("Please fill in all required fields.");
        return;
      }

      try {
        const payload = {
          id: this.selectedArticleId || 0,
          title: this.titleArticle,
          description: this.descriptionArticle,
          url: this.link,
          urlToImage: this.uploadedFileUrl || "",
          fileAwsId: this.uploadedFileId || null,
          status: "Published", // or map your actual status
        };

        const res = await this.$API.post(this.$InsertArticle, payload, {
          withCredentials: true,
          timeout: 600000,
        });

        alert("✅ Article saved successfully! ID: " + res.data);
        this.loadArticles();
      } catch (err) {
        console.error(err);
        alert("⚠️ Failed to save article.");
      }
    },
       // Delete Aktiviti
       async confirmDelete(id) {
  if (!confirm("Are you sure you want to delete this Aktiviti?")) return;
  try {
    const formData = new FormData();
    formData.append("id", id);

    const res = await this.$API.post(this.$DeleteArticles, formData, { withCredentials: true });
    console.log("Delete response:", res.data);

    // Update UI immediately
    this.articles = this.articles.filter(article => article.id !== id);

    alert("✅ Aktiviti deleted successfully!");
  } catch (err) {
    console.error(err);
    alert("⚠️ Failed to delete Aktiviti.");
  }
}
  },
  async mounted() {
    this.loadArticles();
  },
};
</script>
<style scoped>
.cursor-pointer {
  cursor: pointer;
}
.btn-sm {
  padding: 0.25rem 0.5rem;
  font-size: 0.75rem;
  line-height: 1;
  border-radius: 0.2rem;
}
.upload-dropzone {
  border: 1px dashed #ccc;
  padding: 20px;
  text-align: center;
  border-radius: 4px;
  margin-top: 10px;
}
.image-preview img {
  max-width: 100%;
  max-height: 200px;
}
.file-preview {
  display: flex;
  align-items: center;
  margin-top: 5px;
}
.file-preview .remove-file {
  margin-left: 10px;
  cursor: pointer;
}
.upload-submit-btn {
  margin-top: 10px;
}
</style>